import type { Manifest } from '@clawharbor/manifest';
import { fullStylesheet, googleFontHref } from '@clawharbor/render-core';
import { shellStylesheet } from './app-css.js';

/**
 * The manifest paints the page.
 *
 * There is no tenant CSS file anywhere in this app. `fullStylesheet(brand)`
 * emits the tenant's --ch-* custom properties plus the component rules that
 * consume them — the SAME string the MCP Apps widget inlines. That shared call
 * is what makes "one manifest, two surfaces" structural rather than a slogan.
 */

function upsertStyle(id: string, css: string): void {
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

function upsertLink(id: string, href: string, rel = 'stylesheet'): void {
  let el = document.getElementById(id) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.id = id;
    el.rel = rel;
    document.head.appendChild(el);
  }
  if (el.href !== href) el.href = href;
}

export function applyTheme(manifest: Manifest): void {
  /**
   * `contrastSafe` is on because these palettes are HARVESTED. CineStar's real
   * pairing of #ffcb00 on #051851 is fine, but a scraped palette fails WCAG AA
   * more often than not, and shipping unreadable text to prove fidelity to a
   * scrape is the wrong trade.
   */
  upsertStyle('ch-tokens', fullStylesheet(manifest.brand, { contrastSafe: true }));
  // Shell rules last so the page frame wins over the shared component layer.
  upsertStyle('chw-shell', shellStylesheet);

  document.documentElement.style.colorScheme = manifest.brand.mode;
  document.documentElement.lang = manifest.identity.locale || 'en';

  /**
   * Fonts are a licensing decision, so only a declared SUBSTITUTE is ever
   * loaded. `googleFontHref()` returns null unless a family sets
   * `substitute.source === 'google'`, so a proprietary observed face — CineStar's
   * self-hosted DIN Pro — is never fetched and never rehosted.
   */
  const fontHref = googleFontHref(manifest.brand);
  if (fontHref) {
    upsertLink('ch-font-pre', 'https://fonts.gstatic.com/', 'preconnect');
    upsertLink('ch-font', fontHref);
  }

  const name = manifest.identity.displayName;
  document.title = name ? `${name} — unofficial agent app` : 'ClawHarbor';
}

/**
 * Wordmark text, never a logo file.
 *
 * `identity.logo.usage === 'reference-only'` means renderers MUST NOT paint it:
 * reproducing a logo is a copyright act, and for tenants we do not own we set
 * the name in the tenant's own display face instead. This function is the single
 * place that rule is enforced on the web surface.
 */
export function wordmarkText(manifest: Manifest): string {
  const wm = manifest.identity.wordmark;
  if (!wm) return manifest.identity.displayName;
  if (wm.case === 'upper') return wm.text.toUpperCase();
  if (wm.case === 'lower') return wm.text.toLowerCase();
  return wm.text;
}

export function mayPaintLogo(manifest: Manifest): boolean {
  const logo = manifest.identity.logo;
  return logo != null && logo.usage === 'licensed' && manifest.policy.assets.rehostImages;
}
